import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation } from "react-router-dom";

import { Page, Layout, LegacyCard, Card, FormLayout, Text, Select, InlineStack, InlineGrid, BlockStack, Box, TextField, PageActions, Checkbox, Modal, ResourceList, ResourceItem, EmptyState, Icon, LegacyStack, Badge, InlineError, Button, List, ChoiceList, RadioButton, Pagination, Filters, Link, Banner, Tag, Spinner, Tooltip, EmptySearchResult, IndexTable, useIndexResourceState, Divider, TextContainer, SkeletonPage, SkeletonDisplayText, SkeletonBodyText } from '@shopify/polaris';
import { ArrowDownIcon, ArrowUpIcon, RefreshIcon, ExternalSmallIcon, DeleteIcon, DuplicateIcon, SearchIcon, XIcon, PlusIcon, ClipboardIcon, LinkIcon } from '@shopify/polaris-icons';

import { CampaignDiscount } from './CampaignDiscount';
import { SkeletonPageCustom } from './SkeletonPageCustom';
import { DatePickerCustom } from './DatePickerCustom';
import { TimePickerCustom } from './TimePickerCustom';
import { QuantityFieldCustom } from './QuantityFieldCustom';
import { CustomerPickerCustom } from './CustomerPickerCustom';
import { SegmentPickerCustom } from './SegmentPickerCustom';
import { CustomerTagPickerCustom } from './CustomerTagPickerCustom';
import { DiscountTagsPickerCustom } from './DiscountTagsPickerCustom';
import { MoneyFormatCustom } from './MoneyFormatCustom';

import { discountsEmptyStateIconCustom } from "../assets/images";
import { getCurrencySymbol, currencyToFloat, copyToClipboard, defaultInitSettings } from './../Utils';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate, useSafeAppBridge } from "../hooks";

export function Campaign(props) {
    const shopify = useSafeAppBridge();
    const params = useParams();
    const navigate = useHostNavigate();
    const location = useLocation();
    const campaignDiscountRef = useRef();

    const [loading, setLoading] = useState(true);
    const [initState, setInitState] = useState({});
    const [model, setModel] = useState({});
    const [modalEnableDisableActive, setModalEnableDisableActive] = useState(false);
    const [modalDeleteActive, setModalDeleteActive] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [appEnabled, setAppEnabled] = useState(true);
    const [enabledLoading, setEnabledLoading] = useState(false);
    const [stateLoading, setStateLoading] = useState(false);
    const [activeUsageLimit, setActiveUsageLimit] = useState(false);
    const [customerType, setCustomerType] = useState(0);
    const [salesChannel, setSalesChannel] = useState(0);
    const [discountSubTypes] = useState([
        { value: 1, name: "Amount off products", description: "Discount specific products or collections of products." },
        { value: 2, name: "Buy X get Y", description: "Discount products based on a customer's purchase." },
        { value: 3, name: "Amount off order", description: "Discount the total order amount." },
        { value: 4, name: "Shipping discount", description: "Offer shipping discount on an order." },
        { value: 5, name: "Volume discount", description: "Discount products based on a purchase quantity or value." },
        { value: 6, name: "Mix & match", description: "Each product in the set can have its own discount." },
    ]);
    const [error, setError] = useState({ code: false, codeCount: false, codeUnique: false, displayName: false, titleUnique: false, startDate: false, startTime: false, endDate: false, endTime: false, usageLimit: false, customerType: false, wrongStartEndDates: false, codeCountLimit: false, canApplyWithDiscountTags: false, recurringCycleLimit: false });
    const [customerPickerOptions, setCustomerPickerOptions] = useState({ initialQuery: "", initialSelectionIds: [], open: false });
    const [segmentPickerOptions, setSegmentPickerOptions] = useState({ initialQuery: "", initialSelectionIds: [], open: false });
    const [customerTagPickerOptions, setCustomerTagPickerOptions] = useState({ initialQuery: "", open: false });
    const [redirectUrl, setRedirectUrl] = useState('');

    const [initSettings, setInitSettings] = useState(() => {
        const domain = props.user?.myShopifyDomain;

        if (!domain) {
            return defaultInitSettings;
        }

        const stored = localStorage.getItem(`${domain}_initSettings`);

        return JSON.parse(stored || "null") || defaultInitSettings;
    });

    const bulkCodeLimit = 100000;
    const bulkCodeLimitText = '100,000';

    const combinesWithFlags = {
        campaigns: 1 << 0,          // 1
        shippingDiscounts: 1 << 1,  // 2
        productDiscounts: 1 << 2,   // 4
        orderDiscounts: 1 << 3,     // 8
    };
    const [selectedCombinesWith, setSelectedCombinesWith] = useState([]);

    const [modalAddBulkCodesActive, setModalAddBulkCodesActive] = useState(false);
    const [modalViewBulkCodesActive, setModalViewBulkCodesActive] = useState(false);
    const [modalDeleteBulkCodesActive, setModalDeleteBulkCodesActive] = useState(false);
    const [bulkCodesSpecific, setBulkCodesSpecific] = useState('');
    const [bulkCreationChecking, setBulkCreationChecking] = useState(false);
    const [bulkCodes, setBulkCodes] = useState({ items: [], pageInfo: {} });
    const [bulkCodesLoading, setBulkCodesLoading] = useState(false);
    const bulkCreationIntervalRef = useRef();
    const isFetchingRef = useRef(false);
    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(bulkCodes.items);

    const [modalGetShareableLinkActive, setModalGetShareableLinkActive] = useState(false);
    const [combinesWithDisabled, setCombinesWithDisabled] = useState(false);

    const editMode = params.id && params.action !== "duplicate";
    const fetch = FetchCustom();

    const cSharpDateToJsDate = function (val) {
        return new Date(Date.parse(val));
    }

    const isInViewport = function (el) {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    const resetCombinesWith = function (combinesWith) {
        let items = [];
        if (combinesWith & combinesWithFlags.campaigns) {
            items.push(combinesWithFlags.campaigns);
        }
        if (combinesWith & combinesWithFlags.shippingDiscounts) {
            items.push(combinesWithFlags.shippingDiscounts);
        }
        if (combinesWith & combinesWithFlags.productDiscounts) {
            items.push(combinesWithFlags.productDiscounts);
        }
        if (combinesWith & combinesWithFlags.orderDiscounts) {
            items.push(combinesWithFlags.orderDiscounts);
        }

        setSelectedCombinesWith(items);
    }

    const populateData = async () => {
        props.setIsLoading(true);
        let url = 'api/home/campaign';
        if (params.id) {
            url += "?id=" + params.id;

            if (params.action) {
                url += "&action=" + params.action;
            }
        }
        else {
            var query = [];

            if (params.type) {
                query.push("typeId=" + params.type);
            }

            if (params.method) {
                query.push("method=" + params.method);
            }

            if (query.length > 0) {
                url += "?" + query.join("&");
            }
        }

        const data = await fetch(url);

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setInitState(JSON.parse(JSON.stringify(data)));
            setModel(data);
            setLoading(false);
            setActiveUsageLimit(data.usageLimit !== null && parseInt(data.usageLimit) > 0);
            setCustomerType(data.allCustomers ? 0 : (data.customers ? 2 : (data.segments ? 1 : 3)));
            setSalesChannel(data.salesChannels ? parseInt(data.salesChannels) : 0);

            if (data.bulkCreationInProgress) {
                setBulkCreationChecking(true);
            }

            resetCombinesWith(data.combinesWith);

            if (data.campaignMethodId !== 1 && data.campaignMethodId !== 4 && (data.combinesWith & combinesWithFlags.campaigns) !== 0 && !showCombinesWith()) {
                setCombinesWithDisabled(true);
            }
        }
    };

    const getMainThemeSettingsData = async () => {
        const data = await fetch('api/home/getMainThemeSettingsData');

        setEnabledLoading(false);
        setStateLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setAppEnabled(!data.coreEmbedBlockDisabled);
            updateInitSetting('appEnabled', !data.coreEmbedBlockDisabled);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const updateInitSetting = (keyName, value) => {
        if (!props.user?.myShopifyDomain) return;

        const key = `${props.user.myShopifyDomain}_initSettings`;

        setInitSettings(current => {
            const updated = {
                ...current,
                [keyName]: value,
            };

            localStorage.setItem(key, JSON.stringify(updated));

            return updated;
        });
    };

    const recheckBulkCreation = async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        if (!model.bulkCreationInProgress) {
            setBulkCreationChecking(false);
            isFetchingRef.current = false;
            return;
        }

        try {
            const data = await fetch('api/home/recheckBulkCreation', { id: model.id });
            model.codeCount = data.codeCount;
            model.bulkCreationInProgress = data.bulkCreationInProgress;
            setInitState(JSON.parse(JSON.stringify(model)));
            setModel(JSON.parse(JSON.stringify(model)));

            if (!data.bulkCreationInProgress) {
                setBulkCreationChecking(false);
            }
        } catch (error) {
            //console.error(error);
        } finally {
            isFetchingRef.current = false;
        }
    };

    const deleteBulkCodes = async () => {
        model.bulkCreationInProgress = true;
        setBulkCreationChecking(true);
        setModalDeleteBulkCodesActive(false);
        setModalViewBulkCodesActive(false);

        const data = await fetch('api/home/deleteBulkCodes', { campaignId: model.id, all: allResourcesSelected, Ids: selectedResources, totalCount: bulkCodes.totalCount });
        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
    };

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    const saveChanges = async () => {
        var hasError = false;
        if (!error.displayName && !model.displayName) {
            handleError('displayName', true);
            hasError = true;
        }
        if (!error.code && !model.code && model.campaignMethodId === 2) {
            handleError('code', true);
            hasError = true;
        }

        if (!error.codeCount && !model.codeCount && model.campaignMethodId === 3 && !model.bulkCodes?.length && !model.bulkCreationInProgress) {
            handleError('codeCount', true);
            hasError = true;
        }

        //if (!error.displayDescription && !model.displayDescription) {
        //    handleError('displayDescription', true);
        //    hasError = true;
        //}
        if (!error.usageLimit && (!model.usageLimit || parseInt(model.usageLimit) === 0) && activeUsageLimit) {
            handleError('usageLimit', true);
            hasError = true;
        }

        if (!error.recurringCycleLimit && model.recurringCycleOption === 1 && (!model.recurringCycleLimit || parseInt(model.recurringCycleLimit) === 0)) {
            handleError('recurringCycleLimit', true);
            hasError = true;
        }

        if (!error.customerType && ((customerType === 1 && !model.segments) || (customerType === 2 && !model.customers) || (customerType === 3 && !model.customerTags))) {
            handleError('customerType', true);
            hasError = true;
        }

        if (!model.discounts || !model.discounts.length) {
            hasError = true;
            document.querySelector('#discountsEnabledError').style.display = 'flex';
        }

        if (model.hasEndDate) {
            var startDate = cSharpDateToJsDate(model.startDate + ' ' + model.startTime);
            var endDate = cSharpDateToJsDate(model.endDate + ' ' + model.endTime);

            if (startDate > endDate) {
                handleError('wrongStartEndDates', true);
                hasError = true;
            }
        }

        if ((model.combinesWith & combinesWithFlags.productDiscounts) !== 0 && model.allowMultipleDiscountsPerLine && !model.canApplyWithDiscountTags) {
            handleError('canApplyWithDiscountTags', true);
            hasError = true;
        }

        props.setIsLoading(true);

        if (!hasError && model.campaignMethodId === 2 && model.code) {

            const codeIsAvaliable = await fetch('api/home/checkCampaignCode', { id: model.id, code: model.code });

            if (!codeIsAvaliable) {
                handleError('codeUnique', true);
                hasError = true;
            }
        }

        if (!hasError && model.campaignMethodId === 4 && model.displayName) {

            const isAvaliable = await fetch('api/home/checkAutomaticStandaloneTitle', { id: model.id, displayName: model.displayName });

            if (!isAvaliable) {
                handleError('titleUnique', true);
                hasError = true;
            }
        }

        //error.displayDescription ||
        if (hasError || error.code || error.codeCount || error.codeUnique || error.displayName || error.titleUnique || error.startDate || error.startTime || error.endDate || error.endTime || error.usageLimit || error.customerType || error.wrongStartEndDates || error.recurringCycleLimit) {
            setTimeout(function () {
                if (document.querySelector('[aria-invalid="true"]') !== null) {
                    document.querySelector('[aria-invalid="true"]').focus();
                }
                //else if (document.querySelector('#shareOptionsValidError') !== null) {
                //    document.querySelector('#shareOptionsValidError').scrollIntoView();
                //    window.scrollBy(0, -184);
                //}
                else if (document.querySelector('#discountsEnabledError') !== null) {
                    document.querySelector('#discountsEnabledError').scrollIntoView();
                }
            }, 100);

            props.setIsLoading(false);

            setTimeout(function () {
                const uerror = document.getElementById('undefinedError');
                if (uerror) {
                    if (!isInViewport(uerror)) {
                        uerror.scrollIntoView();
                    }
                    uerror.tabIndex = -1;
                    uerror.focus({ preventScroll: true });
                }
            }, 100);

            return;
        }

        setBulkCodesSpecific('');

        if (model.allCustomers) {
            model.customers = null;
            model.segments = null;
            model.customerTags = null;
            model.showBeforeCustomerIdentified = false;
            model.selectedCustomerTags = null;
        }

        if ((model.combinesWith & combinesWithFlags.productDiscounts) === 0 || !model.allowMultipleDiscountsPerLine) {
            model.canApplyWithDiscountTags = null;
        }

        if (model.recurringCycleOption === 0) {
            model.recurringCycleLimit = null;
        }
        else if (model.recurringCycleOption === 2) {
            model.recurringCycleLimit = 0;
        }

        const data = await fetch('api/home/saveCampaign', model);

        props.setIsLoading(false);
        props.setIsDirty(false);

        if (data.redirectUrl) {
            if (data.redirectUrl.indexOf('campaigns/') > -1) {
                props.changeToastActive(true);
            }
            setRedirectUrl(data.redirectUrl);
        }
        else {
            model.statusName = data.statusName;
            model.bulkCreationInProgress = data.bulkCreationInProgress;
            model.bulkCodes = null;
            model.code = model.campaignMethodId === 2 ? model.code : null;
            model.codeCount = model.campaignMethodId === 3 ? model.codeCount : 0;

            if (data.bulkCreationInProgress) {
                setBulkCreationChecking(true);
            }

            //if (model.campaignMethodId === 1) {
            //    model.code = '';
            //    model.codeCount = 0;
            //    model.bulkCreationInProgress = false;
            //}

            setInitState(JSON.parse(JSON.stringify(model)));
            props.changeToastActive(true);
        }
    };

    const goBackOrCampaigns = () => {
        if (location.state?.from === 'home') {
            setRedirectUrl('/');
        } else {
            setRedirectUrl('/campaigns');
        }
    };

    const discardChanges = async () => {
        if (editMode) {
            setModel(Object.assign({}, JSON.parse(JSON.stringify(initState))));
            setCustomerType(initState.allCustomers ? 0 : (initState.customers ? 2 : (initState.segments ? 1 : 3)));
            setSalesChannel(initState.salesChannels ? parseInt(initState.salesChannels) : 0);
            resetCombinesWith(initState.combinesWith);
            setError({ code: false, codeUnique: false, displayName: false, titleUnique: false, startDate: false, startTime: false, endDate: false, endTime: false, usageLimit: false, customerType: false, recurringCycleLimit: false });
            props.setIsDirty(false);
        }
        else {
            props.setIsDirty(false);
            goBackOrCampaigns();
        }
    };

    const orderedDiscounts = function () {
        return model.discounts.sort(function (a, b) {
            return a.seq < b.seq ? -1 : (a.seq > b.seq ? 1 : 0);
        });
    };

    const loadBulkCodes = async (startCursor, endCursor) => {
        setBulkCodesLoading(true);

        const data = await fetch('api/home/bulkCodes', { startCursor: startCursor, endCursor: endCursor, campaignId: model.id });
        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setBulkCodes(data);
            setBulkCodesLoading(false);
        }
    };

    const showCombinesWith = function () {
        return props.user.myShopifyDomain === "dermsilk.myshopify.com"
            || props.user.myShopifyDomain === "frizzandfrillzz.myshopify.com"
            || props.user.myShopifyDomain === "1kkvh1-1s.myshopify.com"
            || props.user.myShopifyDomain === "xfiqs1-bj.myshopify.com"
            || props.user.myShopifyDomain === "gu4dhk-nc.myshopify.com"
            || props.user.myShopifyDomain === "avafinapet.myshopify.com"
            || props.user.myShopifyDomain === "5hinbp-ap.myshopify.com"
            || props.user.myShopifyDomain === "walls-furniture-mattress.myshopify.com"
            || props.user.myShopifyDomain === "crp-mango-plus.myshopify.com"
            || props.user.myShopifyDomain === "totvsrs-martiplast-dc.myshopify.com"
            || props.user.myShopifyDomain === "b-b-labs-cosmeceuticals.myshopify.com"
            || props.user.myShopifyDomain === "gh0dt2-e1.myshopify.com"
            || props.user.myShopifyDomain === "sabal-swim.myshopify.com"
            || props.user.myShopifyDomain === "d2fc10-44.myshopify.com"
            || props.user.myShopifyDomain === "schleich-gmbh.myshopify.com"
            || props.user.myShopifyDomain === "schleich-row.myshopify.com"
            || props.user.myShopifyDomain === "ac2dc7-f5.myshopify.com"
            || props.user.myShopifyDomain === "pt54ne-7i.myshopify.com"
            || props.user.myShopifyDomain === "h-m-demo-ss.myshopify.com"
            || props.user.myShopifyDomain === "share-gmbh.myshopify.com"
            || props.user.myShopifyDomain === "sesto-synergy.myshopify.com"
            || props.user.myShopifyDomain === "1xux5g-08.myshopify.com"
            || props.user.myShopifyDomain === "fitpiggy.myshopify.com"
            || props.user.myShopifyDomain === "willowpump.myshopify.com"
            || props.user.myShopifyDomain === "h05v50-i8.myshopify.com"
            || props.user.myShopifyDomain === "discounts-07.myshopify.com";
    };

    const showRecurringCycleLimit = function () {
        return props.user.myShopifyDomain === "kezynj-j0.myshopify.com"
            || props.user.myShopifyDomain === "discounts-02.myshopify.com"
            || props.user.myShopifyDomain === "discounts-07.myshopify.com";
    };

    const showAutomaticStandalone = function () {
        return props.user.myShopifyDomain === "smartwool-amer-dev.myshopify.com"
            || props.user.myShopifyDomain === "smartwool-amer.myshopify.com"
            || props.user.myShopifyDomain === "discounts-02.myshopify.com"
            || props.user.myShopifyDomain === "discounts-07.myshopify.com";
    };

    const getMinRequirements = function (item) {
        let minRequirements = [];
        if (item.discountSubType !== 5) {
            if (item.minimumAmount) {
                minRequirements.push(1);
            }
            if (item.minimumQuantity) {
                minRequirements.push(2);
            }
        }
        else {
            if (item.tiers[0].minimumAmount) {
                minRequirements.push(1);
            }
            if (item.tiers[0].minimumQuantity) {
                minRequirements.push(2);
            }
        }
        return minRequirements;
    };

    const getMaxLimits = function (item) {
        let maxLimits = [];
        if (item.maximumAmount) {
            maxLimits.push(1);
        }
        if (item.maximumQuantity) {
            maxLimits.push(2);
        }
        if (item.skipQuantity) {
            maxLimits.push(3);
        }
        return maxLimits;
    };

    const handleChange = useCallback(
        (fieldName, newValue) => {
            if (fieldName === 'salePriceHandling') {
                newValue = parseInt(newValue);
            }

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                return model;
            });

            if (fieldName === 'startDate' || fieldName === 'startTime' || fieldName === 'endDate' || fieldName === 'endTime' || fieldName === 'hasEndDate') {
                handleError('wrongStartEndDates', false);
            }
        },
        // eslint-disable-next-line
        [],
    );

    const handleCombinesWithChange = useCallback(
        (newValue) => {
            setSelectedCombinesWith(newValue);
            const combinedValue = newValue.reduce((acc, val) => acc | val, 0);
            handleChange('combinesWith', combinedValue);
        },
        // eslint-disable-next-line
        [],
    );

    const handleDuplicateDiscount = useCallback(
        (index) => {
            var l_discounts = model.discounts;
            var item = l_discounts[index];

            campaignDiscountRef.current.open({
                index: -1,
                discountType: item.discountType,
                discountSubType: item.discountSubType,
                title: 'Copy of ' + item.title,
                valueType: item.valueType,
                valueFixedAmount: item.valueType === 2 ? item.value : '',
                valueFixedPrice: item.valueType === 3 ? item.value : '',
                valuePercentage: item.valueType === 1 ? item.value * 100 : '',
                getType: item.getProductResources !== null && item.getProductResources.length > 0 ? 2 : (item.getCollectionResources !== null && item.getCollectionResources.length > 0 ? 1 : 0),
                buyType: item.buyProductResources !== null && item.buyProductResources.length > 0 ? 2 : (item.buyCollectionResources !== null && item.buyCollectionResources.length > 0 ? 1 : 0),
                excludeType: item.excludeProductResources !== null && item.excludeProductResources.length > 0 ? 2 : (item.excludeCollectionResources !== null && item.excludeCollectionResources.length > 0 ? 1 : 0),
                getCollectionResources: JSON.parse(JSON.stringify(item.getCollectionResources)),
                getProductResources: JSON.parse(JSON.stringify(item.getProductResources)),
                buyCollectionResources: JSON.parse(JSON.stringify(item.buyCollectionResources)),
                buyProductResources: JSON.parse(JSON.stringify(item.buyProductResources)),
                excludeCollectionResources: JSON.parse(JSON.stringify(item.excludeCollectionResources)),
                excludeProductResources: JSON.parse(JSON.stringify(item.excludeProductResources)),
                minRequirements: getMinRequirements(item),
                minimumAmount: item.minimumAmount,
                minimumQuantity: item.minimumQuantity,
                maxLimits: getMaxLimits(item),
                maximumAmount: item.maximumAmount,
                maximumQuantity: item.maximumQuantity,
                skipQuantity: item.skipQuantity,
                onQuantity: item.onQuantity,
                discountedValueType: item.valueType === 2 ? 2 : (item.valueType === 3 ? 4 : (item.valueType === 1 && item.value === 1 ? 3 : 1)),
                setUsesPerOrderLimit: item.usesPerOrderLimit !== null,
                usesPerOrderLimit: item.usesPerOrderLimit,
                appliesOnEachItem: item.appliesOnEachItem !== null ? item.appliesOnEachItem : false,
                countryType: item.allCountries === null || item.allCountries ? 0 : 1,
                setMaximumShippingPrice: item.maximumShippingPrice !== null,
                maximumShippingPrice: item.maximumShippingPrice,
                countries: item.countries,
                includeRestOfWorld: item.includeRestOfWorld,
                shippingMethodType: !item.shippingMethods ? 0 : 1,
                shippingMethods: item.shippingMethods,
                itemRequirements: ((item.buyProductResources !== null && item.buyProductResources.length > 0) || (item.buyCollectionResources !== null && item.buyCollectionResources.length > 0)) ? 1 : (((item.getProductResources !== null && item.getProductResources.length > 0) || (item.getCollectionResources !== null && item.getCollectionResources.length > 0)) ? 2 : 0),
                purchaseType: item.purchaseType,
                autoAdd: item.autoAdd !== null ? item.autoAdd : false,
                tiers: item.discountSubType === 5 ? (item.tiers.map(p => ({
                    title: p.title,
                    minimumAmount: p.minimumAmount,
                    minimumQuantity: p.minimumQuantity,
                    valueFixedAmount: item.valueType === 2 ? p.value : '',
                    valuePercentage: item.valueType === 1 ? p.value * 100 : '',
                }))) : null,
                mixes: item.discountSubType === 6 ? (item.mixes.map(p => ({
                    getType: p.getProductResources !== null && p.getProductResources.length > 0 ? 2 : (p.getCollectionResources !== null && p.getCollectionResources.length > 0 ? 1 : 0),
                    getCollectionResources: JSON.parse(JSON.stringify(p.getCollectionResources)),
                    getProductResources: JSON.parse(JSON.stringify(p.getProductResources)),
                    purchaseType: p.purchaseType,
                    valueType: p.appliesOnEachItem ? 4 : p.valueType,
                    valueFixedAmount: p.valueType === 2 || p.valueType === 4 ? p.value : '',
                    valueFixedPrice: p.valueType === 3 ? p.value : '',
                    valuePercentage: p.valueType === 1 ? p.value * 100 : '',
                    appliesOnEachItem: p.appliesOnEachItem !== null ? p.appliesOnEachItem : false,
                }))) : null
            });
        },
        // eslint-disable-next-line
        [model],
    );

    const handleRemoveDiscount = useCallback(
        (index) => {
            var l_discounts = model.discounts;
            var item = l_discounts[index];

            l_discounts.splice(l_discounts.indexOf(item), 1);
            handleChange('discounts', l_discounts);
        },
        // eslint-disable-next-line
        [model],
    );

    const handleSeqChange = useCallback(
        (e, index, up) => {
            e.stopPropagation();

            var l_discounts = model.discounts;
            var item = l_discounts[index];

            var swapIndex = up ? index - 1 : index + 1;
            if (swapIndex > -1 && swapIndex < l_discounts.length) {
                var itemSwap = l_discounts[swapIndex];

                item.seq = swapIndex + 1;
                itemSwap.seq = index + 1;

                handleChange('discounts', l_discounts);
            }
        },
        // eslint-disable-next-line
        [model],
    );

    const handleSaveDiscount = useCallback(
        (item) => {
            var l_discounts = model.discounts;
            if (!l_discounts) {
                l_discounts = [];
            }

            if (item.index < 0) {
                l_discounts.push({
                    //code: item.title,
                    summary: item.title, //TODO Remove
                    title: item.title,
                    status: 1, //TODO Remove
                    discountType: item.discountType,
                    discountSubType: item.discountSubType,
                    valueType: item.valueType,
                    value: item.valueType === 1 ? parseFloat(item.valuePercentage / 100) : currencyToFloat(item.valueType === 2 ? item.valueFixedAmount : item.valueFixedPrice),
                    onQuantity: item.discountSubType === 2 ? parseInt(item.onQuantity) : null,
                    minimumAmount: item.minRequirements.includes(1) ? currencyToFloat(item.minimumAmount) : null,
                    minimumQuantity: item.minRequirements.includes(2) ? parseInt(item.minimumQuantity) : null,
                    maximumAmount: item.maxLimits.includes(1) ? currencyToFloat(item.maximumAmount) : null,
                    maximumQuantity: item.maxLimits.includes(2) ? parseInt(item.maximumQuantity) : null,
                    skipQuantity: item.maxLimits.includes(3) ? parseInt(item.skipQuantity) : null,
                    getCollectionResources: item.getCollectionResources,
                    getProductResources: item.getProductResources,
                    buyCollectionResources: item.buyCollectionResources,
                    buyProductResources: item.buyProductResources,
                    excludeCollectionResources: item.excludeCollectionResources,
                    excludeProductResources: item.excludeProductResources,
                    //buyCollections: item.buyCollections,
                    //getCollections: item.getCollections,
                    //buyProducts: item.buyProducts,
                    //getProducts: item.getProducts,
                    //buyVariants: item.buyVariants,
                    //getVariants: item.getVariants,
                    appliesOnEachItem: item.discountSubType === 1 && item.valueType === 2 ? item.appliesOnEachItem : null,
                    //usageLimit: item.usageLimit,
                    //usageCount: item.usageCount,
                    //appliesOncePerCustomer: item.appliesOncePerCustomer,
                    maximumShippingPrice: item.setMaximumShippingPrice ? currencyToFloat(item.maximumShippingPrice) : null,
                    usesPerOrderLimit: item.setUsesPerOrderLimit ? parseInt(item.usesPerOrderLimit) : null,
                    allCustomers: true, //TODO Remove
                    //segments: item.segments,
                    //customers: item.customers,
                    allCountries: item.countryType === null || item.countryType === 0,
                    countries: item.countryType === 1 ? item.countries : null,
                    includeRestOfWorld: item.countryType === 1 ? item.includeRestOfWorld : null,
                    shippingMethods: item.shippingMethodType === 1 ? item.shippingMethods : null,
                    purchaseType: item.purchaseType,
                    autoAdd: item.discountSubType === 2 ? item.autoAdd : null,
                    tiers: item.discountSubType === 5 ? item.tiers.map(p => ({
                        id: p.id,
                        title: p.title,
                        value: item.valueType === 1 ? parseFloat(p.valuePercentage / 100) : currencyToFloat(p.valueFixedAmount),
                        minimumQuantity: item.minRequirements.includes(2) ? parseInt(p.minimumQuantity) : null,
                        minimumAmount: item.minRequirements.includes(1) ? currencyToFloat(p.minimumAmount) : null,
                    })) : null,
                    mixes: item.discountSubType === 6 ? item.mixes.map(p => ({
                        id: p.id,
                        getCollectionResources: p.getCollectionResources,
                        getProductResources: p.getProductResources,
                        purchaseType: p.purchaseType,
                        valueType: p.valueType !== 4 ? p.valueType : 2,
                        value: p.valueType === 1 ? parseFloat(p.valuePercentage / 100) : currencyToFloat(p.valueType === 2 || p.valueType === 4 ? p.valueFixedAmount : p.valueFixedPrice),
                        appliesOnEachItem: p.valueType === 2 ? false : (p.valueType === 4 ? true : null),
                    })) : null,
                    seq: l_discounts.length + 1
                });
            }
            else {
                l_discounts[item.index].title = item.title;
                l_discounts[item.index].summary = item.title; //TODO Remove
                l_discounts[item.index].valueType = item.valueType;
                l_discounts[item.index].value = item.valueType === 1 ? parseFloat(item.valuePercentage / 100) : currencyToFloat(item.valueType === 2 ? item.valueFixedAmount : item.valueFixedPrice);
                l_discounts[item.index].minimumAmount = item.minRequirements.includes(1) ? currencyToFloat(item.minimumAmount) : null;
                l_discounts[item.index].minimumQuantity = item.minRequirements.includes(2) ? parseInt(item.minimumQuantity) : null;
                l_discounts[item.index].maximumAmount = item.maxLimits.includes(1) ? currencyToFloat(item.maximumAmount) : null;
                l_discounts[item.index].maximumQuantity = item.maxLimits.includes(2) ? parseInt(item.maximumQuantity) : null;
                l_discounts[item.index].skipQuantity = item.maxLimits.includes(3) ? parseInt(item.skipQuantity) : null;
                l_discounts[item.index].onQuantity = item.discountSubType === 2 ? parseInt(item.onQuantity) : null;
                l_discounts[item.index].getCollectionResources = item.getCollectionResources;
                l_discounts[item.index].getProductResources = item.getProductResources;
                l_discounts[item.index].buyCollectionResources = item.buyCollectionResources;
                l_discounts[item.index].buyProductResources = item.buyProductResources;
                l_discounts[item.index].excludeCollectionResources = item.excludeCollectionResources;
                l_discounts[item.index].excludeProductResources = item.excludeProductResources;
                l_discounts[item.index].usesPerOrderLimit = item.setUsesPerOrderLimit ? parseInt(item.usesPerOrderLimit) : null;
                l_discounts[item.index].appliesOnEachItem = item.discountSubType === 1 && item.valueType === 2 ? item.appliesOnEachItem : null;
                l_discounts[item.index].allCountries = item.countryType === null || item.countryType === 0;
                l_discounts[item.index].maximumShippingPrice = item.setMaximumShippingPrice ? currencyToFloat(item.maximumShippingPrice) : null;
                l_discounts[item.index].countries = item.countryType === 1 ? item.countries : null;
                l_discounts[item.index].includeRestOfWorld = item.countryType === 1 ? item.includeRestOfWorld : null;
                l_discounts[item.index].shippingMethods = item.shippingMethodType === 1 ? item.shippingMethods : null;
                l_discounts[item.index].purchaseType = item.purchaseType;
                l_discounts[item.index].autoAdd = item.discountSubType === 2 ? item.autoAdd : null;
                l_discounts[item.index].tiers = item.discountSubType === 5 ? item.tiers.map(p => ({
                    id: p.id,
                    title: p.title,
                    value: item.valueType === 1 ? parseFloat(p.valuePercentage / 100) : currencyToFloat(p.valueFixedAmount),
                    minimumQuantity: item.minRequirements.includes(2) ? parseInt(p.minimumQuantity) : null,
                    minimumAmount: item.minRequirements.includes(1) ? currencyToFloat(p.minimumAmount) : null,
                })) : null;
                l_discounts[item.index].mixes = item.discountSubType === 6 ? item.mixes.map(p => ({
                    id: p.id,
                    getCollectionResources: p.getCollectionResources,
                    getProductResources: p.getProductResources,
                    purchaseType: p.purchaseType,
                    valueType: p.valueType !== 4 ? p.valueType : 2,
                    value: p.valueType === 1 ? parseFloat(p.valuePercentage / 100) : currencyToFloat(p.valueType === 2 || p.valueType === 4 ? p.valueFixedAmount : p.valueFixedPrice),
                    appliesOnEachItem: p.valueType === 2 ? false : (p.valueType === 4 ? true : null),
                })) : null;
            }

            handleChange('discounts', l_discounts);
        },
        // eslint-disable-next-line
        [model],
    );

    const handleEnableDisable = useCallback(
        () => {
            setProcessing(true);
            props.setIsLoading(true);

            var enable = model.statusName === 'Expired';
            if (enable) {
                setModel((prevState) => {
                    let model = Object.assign({}, prevState);
                    model['hasEndDate'] = false;
                    return model;
                });
            }
            else {
                setModel((prevState) => {
                    let model = Object.assign({}, prevState);
                    model['hasEndDate'] = true;
                    model['endDate'] = model['startDate'];
                    model['endTime'] = model['startTime'];
                    return model;
                });
            }
        },
        [model, props],
    );

    const handleActiveUsageLimit = useCallback(
        (newValue) => {
            setActiveUsageLimit(newValue);
            handleChange('usageLimit', null);
        },
        [handleChange],
    );

    const handleCustomerPicker = useCallback(
        (fieldName, newValue) => {
            setCustomerPickerOptions((prevState) => {
                let item = Object.assign({}, prevState);
                item[fieldName] = newValue;
                return item;
            });
        },
        [],
    );

    const handleSegmentPicker = useCallback(
        (fieldName, newValue) => {
            setSegmentPickerOptions((prevState) => {
                let item = Object.assign({}, prevState);
                item[fieldName] = newValue;
                return item;
            });
        },
        [],
    );

    const handleCustomerTagPicker = useCallback(
        (fieldName, newValue) => {
            setCustomerTagPickerOptions((prevState) => {
                let item = Object.assign({}, prevState);
                item[fieldName] = newValue;
                return item;
            });
        },
        [],
    );

    const toggleModalEnableDisable = useCallback(
        () => setModalEnableDisableActive(!modalEnableDisableActive),
        [modalEnableDisableActive],
    );

    const toggleModalDelete = useCallback(
        () => setModalDeleteActive(!modalDeleteActive),
        [modalDeleteActive],
    );

    const enableDisableCallback = async () => {
        var enable = model.statusName === 'Expired';

        const data = await fetch('api/home/saveCampaign', model);

        setProcessing(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            var message = "Campaign " + (enable ? "activated" : "deactivated");

            model.statusName = data.statusName;
            setInitState(JSON.parse(JSON.stringify(model)));
            setModel(JSON.parse(JSON.stringify(model)));

            toggleModalEnableDisable();
            props.changeToastActive(true, message);
        }
    };

    const deleteCampaigns = async () => {
        setProcessing(true);
        props.setIsLoading(true);

        const data = await fetch('api/home/deleteCampaigns', [model.id]);

        setProcessing(false);
        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            toggleModalDelete();
            props.changeToastActive(true, "Campaign deleted");
            setRedirectUrl('/campaigns');
        }
    };

    useEffect(() => {
        document.title = "Campaign | Stack Discounts";
        populateData();
        getMainThemeSettingsData();
        getUser();
        // eslint-disable-next-line
    }, [params.id, params.action]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}' && editMode) {
            props.setIsDirty(JSON.stringify(initState) !== JSON.stringify(model));
        }
        else if (!editMode) {
            props.setIsDirty(true);
        }
        // eslint-disable-next-line
    }, [model, initState]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}' && (initState.hasEndDate !== model.hasEndDate || initState.endDate !== model.endDate) && modalEnableDisableActive) {
            enableDisableCallback();
        }
        // eslint-disable-next-line
    }, [model.hasEndDate, model.endDate]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}' && (initState.campaignMethodId !== model.campaignMethodId || initState.code !== model.code)) {
            handleError('code', false);
            handleError('codeUnique', false);
        }
        // eslint-disable-next-line
    }, [model.campaignMethodId, model.code]);

    useEffect(() => {
        if (JSON.stringify(model) !== '{}' && (initState.campaignMethodId !== model.campaignMethodId || initState.displayName !== model.displayName)) {
            handleError('titleUnique', false);
        }
        // eslint-disable-next-line
    }, [model.campaignMethodId, model.displayName]);

    useEffect(() => {
        if (props.triggerSave) {
            props.setTriggerSave(false);
            saveChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerSave]);

    useEffect(() => {
        if (props.triggerDiscard) {
            props.setTriggerDiscard(false);
            discardChanges();
        }
        // eslint-disable-next-line
    }, [props.triggerDiscard]);

    useEffect(() => {
        if (redirectUrl) {
            setTimeout(() => {
                navigate(redirectUrl);
                setRedirectUrl('');
            }, 0);
        }
    }, [redirectUrl, navigate]);

    useEffect(() => {
        if (bulkCreationChecking) {
            bulkCreationIntervalRef.current = setInterval(() => {
                recheckBulkCreation();
            }, 5000);
        } else if (bulkCreationIntervalRef.current) {
            clearInterval(bulkCreationIntervalRef.current);
        }
        return () => clearInterval(bulkCreationIntervalRef.current);
        // eslint-disable-next-line
    }, [bulkCreationChecking]);

    useEffect(() => {
        const backdrops = document.querySelectorAll('.Polaris-Backdrop');
        if (backdrops.length > 1) {
            backdrops[1].style.zIndex = '2000000001';
        }
        const modals = document.querySelectorAll('.Polaris-Modal-Dialog__Container');
        if (modals.length > 1) {
            modals[1].style.zIndex = '2000000002';
        }
    }, [modalDeleteBulkCodesActive]);

    const modalEnableDisable = (
        <Modal
            title={(model.statusName === 'Expired' ? 'Activate ' : 'Deactivate ') + model.displayName + '?'}
            open={modalEnableDisableActive}
            onClose={toggleModalEnableDisable}
            primaryAction={{
                content: model.statusName === 'Expired' ? 'Activate' : 'Deactivate',
                onAction: () => { handleEnableDisable() },
                loading: processing
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModalEnableDisable
                },
            ]}
        >
            <div id="modal-enable-disable-campaign">
                <Modal.Section>
                    {model.statusName === 'Expired' ? 'This campaign will become active now and will have no end date.' : 'This campaign will expire now.'}
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalDelete = (
        <Modal
            title={'Delete ' + model.displayName + '?'}
            open={modalDeleteActive}
            onClose={toggleModalDelete}
            primaryAction={{
                content: 'Delete campaign',
                onAction: () => { deleteCampaigns() },
                destructive: true,
                loading: processing
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModalDelete
                },
            ]}
        >
            <div id="modal-delete-campaign">
                <Modal.Section>
                    <Text as="p">
                        Are you sure you want to delete the campaign <Text as="span" fontWeight="semibold">{model.displayName}</Text>? This can’t be undone.
                    </Text>
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalAddBulkCodes = (
        <Modal
            title="Add codes to campaign"
            open={modalAddBulkCodesActive}
            onClose={() => {
                setModalAddBulkCodesActive(false);
                handleError('codeCountLimit', false);
                setBulkCodesSpecific(model.bulkCodes?.length ? model.bulkCodes.join("\n") : '');
            }}
            primaryAction={{
                content: 'Apply',
                onAction: () => {
                    let codes = bulkCodesSpecific.trim().split(/\r?\n/).map(line => line.trim()).filter(line => line !== '');
                    if (codes.length > bulkCodeLimit) {
                        handleError('codeCountLimit', true);
                    }
                    else {
                        handleChange('bulkCodes', codes);
                        handleError('codeCount', false);
                        handleError('codeCountLimit', false);
                        setModalAddBulkCodesActive(false);
                    }
                }
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => {
                        setModalAddBulkCodesActive(false);
                        handleError('codeCountLimit', false);
                        setBulkCodesSpecific(model.bulkCodes?.length ? model.bulkCodes.join("\n") : '');
                    },
                }
            ]}
        >
            <div id="add-codes-modal">
                <Modal.Section>
                    <BlockStack gap="400">
                        <ChoiceList
                            choices={[
                                { label: 'Generate random codes (coming soon)', value: 1, disabled: true },
                                { label: 'Provide specific codes', value: 2 },
                            ]}
                            selected={[model.bulkCodeGenerationType]}
                            onChange={(newValue) => handleChange('bulkCodeGenerationType', newValue[0])}
                        />
                        <TextField
                            value={bulkCodesSpecific}
                            onChange={(newValue) => setBulkCodesSpecific(newValue)}
                            label="Codes to add (one code per line)"
                            multiline={10}
                            helpText={`Add up to ${bulkCodeLimitText} codes at a time.`}
                            error={error.codeCountLimit ? `You can only add up to ${bulkCodeLimitText} codes at a time` : ''}
                            onBlur={() => {
                                let codes = bulkCodesSpecific.trim().split(/\r?\n/).map(line => line.trim()).filter(line => line !== '');
                                handleError('codeCountLimit', codes.length > bulkCodeLimit);
                            }}
                        />
                        <Banner status="warning">
                            <p>Only unique codes will be added. Existing codes will be skipped.</p>
                        </Banner>
                    </BlockStack>
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalViewBulkCodes = (
        <Modal
            title="Codes"
            open={modalViewBulkCodesActive}
            onClose={() => {
                setModalViewBulkCodesActive(false);
            }}
            secondaryActions={[
                {
                    content: 'Close',
                    onAction: () => {
                        setModalViewBulkCodesActive(false);
                    },
                }
            ]}
        >
            <div id="view-codes-modal">
                <IndexTable
                    emptyState={
                        <Box paddingBlockStart="1200">
                            <EmptySearchResult
                                title={'No codes found'}
                                description={'Try changing the filters or search term'}
                                withIllustration={false}
                            />
                        </Box>
                    }
                    resourceName={{ singular: 'code', plural: 'codes' }}
                    itemCount={bulkCodes.items.length}
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    onSelectionChange={handleSelectionChange}
                    hasMoreItems={bulkCodes.totalCount > 50}
                    promotedBulkActions={[
                        {
                            destructive: true,
                            content: 'Delete codes',
                            onAction: () => {
                                if (props.user.viewOnly) return;
                                setModalDeleteBulkCodesActive(true);
                            },
                        }
                    ]}
                    headings={[{ title: 'Code' }, { title: 'Used' }]}
                    loading={bulkCodesLoading}
                >
                    {
                        bulkCodes.items.map(
                            (
                                { id, code, usageCount },
                                index,
                            ) => (
                                <IndexTable.Row
                                    id={id}
                                    key={id}
                                    selected={selectedResources.includes(id)}
                                    position={index}
                                >
                                    <IndexTable.Cell>
                                        <InlineStack gap="200" blockAlign="center">
                                            <Text variant="bodyMd" as="span">{code}</Text>
                                            <div className="resource-item-copy-code display-flex-span">
                                                <Tooltip content="Copy code">
                                                    <Button disabled={!code} variant="tertiary" icon={ClipboardIcon} onClick={(e) => copyToClipboard(code ?? '', e)} />
                                                </Tooltip>
                                            </div>
                                        </InlineStack>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Text variant="bodyMd" as="span">{usageCount + (bulkCodes.usageLimit ? `/${bulkCodes.usageLimit}` : '')}</Text>
                                    </IndexTable.Cell>
                                </IndexTable.Row>
                            ),
                        )
                    }
                </IndexTable>
                <div className="paging-fixed">
                    <Pagination
                        hasPrevious={bulkCodes.pageInfo.hasPreviousPage}
                        hasNext={bulkCodes.pageInfo.hasNextPage}
                        onPrevious={() => {
                            loadBulkCodes(bulkCodes.pageInfo.startCursor, '');
                        }}
                        onNext={() => {
                            loadBulkCodes('', bulkCodes.pageInfo.endCursor);
                        }}
                    />
                </div>
            </div>
        </Modal>
    );

    const modalDeleteBulkCodes = (
        <Modal
            size="small"
            title={"Delete " + (selectedResources.length > 1 ? (allResourcesSelected ? bulkCodes.totalCount : selectedResources.length) + " " : "") + "codes" + (selectedResources.length > 1 ? "s" : "") + "?"
            }
            open={modalDeleteBulkCodesActive}
            onClose={() => {
                setModalDeleteBulkCodesActive(false);
            }}
            primaryAction={{
                content: 'Delete',
                onAction: () => { deleteBulkCodes() },
                destructive: true,
                //loading: deleting
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => {
                        setModalDeleteBulkCodesActive(false);
                    },
                },
            ]}
        >
            <div id="modal-delete-codes">
                <Modal.Section>
                    Deleted campaign codes cannot be recovered. <br /> Do you still want to continue?
                </Modal.Section>
            </div>
        </Modal>
    );

    const modalGetShareableLink = editMode && model.campaignMethodId === 2 ? (
        <Modal
            title={'Get a shareable link'}
            open={modalGetShareableLinkActive}
            onClose={() => {
                setModalGetShareableLinkActive(false);
            }}
            secondaryActions={[
                {
                    content: 'Close',
                    onAction: () => {
                        setModalGetShareableLinkActive(false);
                    },
                },
            ]}
        >
            <div id="modal-get-shareable-link">
                <Modal.Section>
                    <BlockStack gap="500">
                        You can share this discount link with your customers via email or social media. This discount will be applied at checkout when customers use this link.
                        <div className="no-focus">
                            <TextField value={model.code ? `https://${props.user.domain ?? props.user.myShopifyDomain}/discount/${model.code}` : ''} readOnly selectTextOnFocus
                                connectedRight={
                                    <Tooltip content="Copy">
                                        <Button disabled={!model.code} size="large" icon={ClipboardIcon} onClick={(e) => copyToClipboard(`https://${props.user.domain ?? props.user.myShopifyDomain}/discount/${model.code}` ?? '', e)} />
                                    </Tooltip>
                                }
                            />
                        </div>
                    </BlockStack>
                </Modal.Section>
            </div>
        </Modal>
    ) : null;

    const removeCustomerTag = useCallback(
        (item) => () => {
            let ids = model.customerTags.split(',').map(p => p);
            ids.splice(ids.indexOf(item), 1);
            handleChange('customerTags', ids.join());

            let sItems = model.selectedCustomerTags;
            sItems.splice(sItems.indexOf(item), 1);
            handleChange('selectedCustomerTags', sItems);

        },
        [model.customerTags, model.selectedCustomerTags, handleChange],
    );

    const onlyOnlineStore = useCallback(
        (isSelected) =>
            isSelected && (
                <Box paddingBlockEnd="200">
                    <Text as="p" tone="subdued">Only applies to purchases made through your Online Store.</Text>
                </Box>
            ),
        [],
    );

    const expectOnlineStore = useCallback(
        (isSelected) =>
            isSelected && (
                <Box paddingBlockEnd="200">
                    <Text as="p" tone="subdued">Applies to all channels except your Online Store (e.g., POS, social media).</Text>
                </Box>
            ),
        [],
    );

    const renderCustomerSegments = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <Box paddingBlock="200">
                    <BlockStack gap="200">
                        <TextField value={''}
                            placeholder={'Search customer segments'}
                            prefix={<Icon source={SearchIcon} tone="subdued" />}
                            error={error.customerType ? 'Specific customer segments must be added' : ''}
                            connectedRight={<Button size="large" onClick={() => {
                                setSegmentPickerOptions({
                                    initialQuery: "",
                                    initialSelectionIds: model.segments ? model.segments.split(',').map(p => `segment_${p}`) : [],
                                    open: true
                                })
                            }}>Browse</Button>}
                            onChange={(newValue) => {
                                setSegmentPickerOptions({
                                    initialQuery: newValue,
                                    initialSelectionIds: model.segments ? model.segments.split(',').map(p => `segment_${p}`) : [],
                                    open: true
                                })
                            }}
                        />
                        <SegmentPickerCustom
                            open={segmentPickerOptions.open}
                            editing={model.segments ? true : false}
                            isEmbed={props.isEmbed}
                            initialQuery={segmentPickerOptions.initialQuery}
                            initialSelectionIds={segmentPickerOptions.initialSelectionIds}
                            selectedSegments={model.selectedSegments}
                            user={props.user}
                            handleSegmentPicker={handleSegmentPicker}
                            handleChange={handleChange}
                            handleError={handleError}
                        />
                        {
                            model.segments ?
                                <div className="selected-resources">
                                    <ResourceList
                                        items={model.selectedSegments}
                                        renderItem={(item, id, index) => {
                                            return (
                                                <ResourceItem
                                                    verticalAlignment="center"
                                                >
                                                    <InlineStack wrap={false}>
                                                        <div className="w-100">
                                                            <InlineStack gap="400">
                                                                <a href={props.user.shopifyAdminUrl + "/customers/segments/" + item.id} target="_blank" rel="noreferrer" className="Polaris-Link">{item.name}</a>
                                                            </InlineStack>
                                                        </div>
                                                        <div>
                                                            <Button variant="monochromePlain" onClick={() => {
                                                                let ids = model.segments.split(',').map(p => parseFloat(p));
                                                                ids.splice(ids.indexOf(item.id), 1);
                                                                handleChange('segments', ids.join());

                                                                let sItems = model.selectedSegments;
                                                                sItems.splice(sItems.indexOf(sItems.filter(p => p.id === item.id)[0]), 1);
                                                                handleChange('selectedSegments', sItems);
                                                            }}><Icon source={XIcon} /></Button>
                                                        </div>
                                                    </InlineStack>
                                                </ResourceItem>
                                            );
                                        }}
                                    />
                                </div>
                                : null
                        }
                    </BlockStack>
                </Box>
            ),
        [model, error.customerType, props.user, props.isEmbed, segmentPickerOptions, handleSegmentPicker, handleChange, handleError],
    );

    const renderCustomerTags = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <Box paddingBlock="200">
                    <BlockStack gap="200">
                        <TextField value={''}
                            placeholder={'Search customer tags'}
                            prefix={<Icon source={SearchIcon} tone="subdued" />}
                            error={error.customerType ? 'Specific customer tags must be added' : ''}
                            connectedRight={<Button size="large" onClick={() => {
                                setCustomerTagPickerOptions({
                                    initialQuery: "",
                                    open: true
                                })
                            }}>Browse</Button>}
                            onChange={(newValue) => {
                                setCustomerTagPickerOptions({
                                    initialQuery: newValue,
                                    open: true
                                })
                            }}
                        />
                        <CustomerTagPickerCustom
                            open={customerTagPickerOptions.open}
                            editing={model.customerTags ? true : false}
                            isEmbed={props.isEmbed}
                            initialQuery={customerTagPickerOptions.initialQuery}
                            selectedCustomerTags={model.selectedCustomerTags}
                            user={props.user}
                            handleCustomerTagPicker={handleCustomerTagPicker}
                            handleChange={handleChange}
                            handleError={handleError}
                        />
                        {
                            model.customerTags ?
                                <InlineStack gap="100">
                                    {
                                        (model.selectedCustomerTags ?? []).map((option) => (
                                            <Tag key={option} onRemove={removeCustomerTag(option)}>{option}</Tag>
                                        ))
                                    }
                                </InlineStack>
                                : null
                        }
                    </BlockStack>
                </Box>
            ),
        [model, error.customerType, props.user, props.isEmbed, customerTagPickerOptions, handleCustomerTagPicker, handleChange, handleError, removeCustomerTag],
    );

    const renderCustomers = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <Box paddingBlock="200">
                    <BlockStack gap="200">
                        <TextField value={''}
                            placeholder={'Search customers'}
                            prefix={<Icon source={SearchIcon} tone="subdued" />}
                            error={error.customerType ? 'Specific customers must be added' : ''}
                            connectedRight={<Button size="large" onClick={() => {
                                setCustomerPickerOptions({
                                    initialQuery: "",
                                    initialSelectionIds: model.customers ? model.customers.split(',').map(p => `customer_${p}`) : [],
                                    open: true
                                })
                            }}>Browse</Button>}
                            onChange={(newValue) => {
                                setCustomerPickerOptions({
                                    initialQuery: newValue,
                                    initialSelectionIds: model.customers ? model.customers.split(',').map(p => `customer_${p}`) : [],
                                    open: true
                                })
                            }}
                        />
                        <CustomerPickerCustom
                            open={customerPickerOptions.open}
                            editing={model.customers ? true : false}
                            isEmbed={props.isEmbed}
                            initialQuery={customerPickerOptions.initialQuery}
                            initialSelectionIds={customerPickerOptions.initialSelectionIds}
                            selectedCustomers={model.selectedCustomers}
                            user={props.user}
                            handleCustomerPicker={handleCustomerPicker}
                            handleChange={handleChange}
                            handleError={handleError}
                        />
                        {
                            model.customers ?
                                <div className="selected-resources">
                                    <ResourceList
                                        items={model.selectedCustomers}
                                        renderItem={(item, id, index) => {
                                            return (
                                                <ResourceItem
                                                    verticalAlignment="center"
                                                >
                                                    <InlineStack wrap={false}>
                                                        <div className="w-100">
                                                            <InlineStack gap="400">
                                                                <Text>{!item.firstName && !item.lastName ? item.email : (item.firstName + " " + item.lastName).trim()}</Text>
                                                                <Text as="p" tone="subdued">{item.email}</Text>
                                                            </InlineStack>
                                                        </div>
                                                        <div>
                                                            <Button variant="monochromePlain" onClick={() => {
                                                                let ids = model.customers.split(',').map(p => parseFloat(p));
                                                                ids.splice(ids.indexOf(item.id), 1);
                                                                handleChange('customers', ids.join());

                                                                let sItems = model.selectedCustomers;
                                                                sItems.splice(sItems.indexOf(sItems.filter(p => p.id === item.id)[0]), 1);
                                                                handleChange('selectedCustomers', sItems);
                                                            }}><Icon source={XIcon} /></Button>
                                                        </div>
                                                    </InlineStack>
                                                </ResourceItem>
                                            );
                                        }}
                                    />
                                </div>
                                : null
                        }
                    </BlockStack>
                </Box>
            ),
        [model, error.customerType, props.user, props.isEmbed, customerPickerOptions, handleCustomerPicker, handleChange, handleError],
    );

    const customerTypes = [{ label: 'All customers', value: 0 }];
    customerTypes.push({ label: 'Specific customer segments', value: 1, renderChildren: renderCustomerSegments });
    customerTypes.push({ label: 'Specific customer tags', value: 3, renderChildren: renderCustomerTags });
    customerTypes.push({ label: 'Specific customers', value: 2, renderChildren: renderCustomers });

    let secondaryActions = [];
    if (editMode) {
        secondaryActions.push({
            content: 'Duplicate',
            icon: DuplicateIcon,
            onAction: async () => {
                if (props.user.viewOnly) return;
                if (shopify) {
                    let saveBar = document.getElementById('bridge-save-bar');
                    if (saveBar != null && saveBar.showing) {
                        await shopify.saveBar.leaveConfirmation();
                    }
                }
                setRedirectUrl(`/campaigns/${params.id}/duplicate`);
            }
        });
    }

    let actionGroups = [];
    if (editMode && model.statusName !== 'Expired' && model.campaignMethodId === 2) {
        actionGroups.push({
            title: 'Promote',
            actions: [
                {
                    content: 'Get a shareable link',
                    icon: LinkIcon,
                    accessibilityLabel: 'Get a shareable link',
                    onAction: () => setModalGetShareableLinkActive(true),
                },
            ],
        });
    }

    if (editMode) {
        actionGroups.push({
            title: 'More actions',
            actions: [
                {
                    content: model.statusName === 'Expired' ? 'Activate' : 'Deactivate',
                    accessibilityLabel: model.statusName === 'Expired' ? 'Activate' : 'Deactivate',
                    onAction: () => {
                        if (props.user.viewOnly) return;
                        toggleModalEnableDisable();
                    }
                },
                {
                    content: 'Delete',
                    accessibilityLabel: 'Delete',
                    destructive: true,
                    onAction: () => {
                        if (props.user.viewOnly) return;
                        toggleModalDelete();
                    }
                },
            ],
        });
    }

    const renderApplyWithDiscountTags = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <Box paddingBlock="200">
                    <BlockStack gap="200">
                        <Checkbox
                            label="Allow multiple discounts on the same cart line"
                            checked={model.allowMultipleDiscountsPerLine}
                            onChange={(newValue) => {
                                handleChange('allowMultipleDiscountsPerLine', newValue);
                            }}
                        />
                        {
                            model.allowMultipleDiscountsPerLine ?
                                <DiscountTagsPickerCustom
                                    fieldName="canApplyWithDiscountTags"
                                    value={model.canApplyWithDiscountTags}
                                    label="Can combine with campaigns tagged:"
                                    placeholder="Select tags"
                                    helpText="The other campaigns must also allow one of this campaign's tags."
                                    handleChange={handleChange}
                                    handleError={handleError}
                                    allowAddNew={false}
                                    error={error.canApplyWithDiscountTags ? 'Select at least one tag' : ''}
                                />
                                : null
                        }

                    </BlockStack>
                </Box>
            ),
        [model, handleChange, handleError, error.canApplyWithDiscountTags],
    );

    const renderRecurringCycleLimit = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <Box paddingBlockEnd="100">
                    <div className="choice-list-input">
                        <QuantityFieldCustom
                            fieldName="recurringCycleLimit"
                            value={model.recurringCycleLimit}
                            required={true}
                            error={error.recurringCycleLimit ? "Discount limit can't be blank" : ''}
                            handleChange={handleChange}
                            handleError={handleError}
                        />
                    </div>
                </Box>
            ),
        [model, handleChange, handleError, error.recurringCycleLimit],
    );

    return (
        loading ?
            <SkeletonPage primaryAction backAction>
                <Layout>
                    {
                        !initSettings.appEnabled || !appEnabled ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <SkeletonDisplayText size="small" />
                                            <SkeletonBodyText lines={2} />
                                        </BlockStack>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    <Layout.Section>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={7} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={8} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={9} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={8} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={6} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={8} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={6} />
                            </TextContainer>
                        </LegacyCard>
                        <LegacyCard sectioned>
                            <TextContainer>
                                <SkeletonDisplayText size="small" />
                                <SkeletonBodyText lines={8} />
                            </TextContainer>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section variant="oneThird">
                        <LegacyCard>
                            <LegacyCard.Section>
                                <TextContainer>
                                    <SkeletonDisplayText size="small" />
                                    <SkeletonDisplayText size="small" />
                                    <SkeletonBodyText lines={3} />
                                </TextContainer>
                            </LegacyCard.Section>
                            <LegacyCard.Section>
                                <TextContainer>
                                    <SkeletonDisplayText size="small" />
                                    <SkeletonBodyText lines={4} />
                                </TextContainer>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
            :
            <Page title={editMode ? model.displayName : (params.action !== "duplicate" ? "Create campaign" : "Duplicate campaign")}
                backAction={{
                    content: 'Campaigns', onAction: async () => {
                        if (!params.id && !initState.title && JSON.stringify(initState) === JSON.stringify(model)) {
                            props.setIsDirty(false);
                        }
                        else if (shopify) {
                            let saveBar = document.getElementById('bridge-save-bar');
                            if (saveBar != null && saveBar.showing) {
                                await shopify.saveBar.leaveConfirmation();
                            }
                        }
                        goBackOrCampaigns();
                    }
                }}
                secondaryActions={secondaryActions}
                actionGroups={actionGroups}
            >
                {modalEnableDisable}
                {modalDelete}
                {modalAddBulkCodes}
                {modalViewBulkCodes}
                {modalDeleteBulkCodes}
                {modalGetShareableLink}
                <Layout>
                    {
                        !initSettings.appEnabled || !appEnabled ?
                            <Layout.Section>
                                <div className="card-main">
                                    <Card>
                                        <BlockStack gap="300">
                                            <div className="qs-title">
                                                <Text as="h2" variant="headingMd">
                                                    Enabling required!
                                                </Text>
                                            </div>
                                            <Banner tone={enabledLoading ? 'warning' : 'critical'}>
                                                <InlineStack align="space-between" blockAlign="center" gap="200">
                                                    <BlockStack>
                                                        <Text as="p">
                                                            {
                                                                enabledLoading ?
                                                                    'Recheck state button will update the current state of the app.' :
                                                                    'This step is required only for syncing with your storefront.'
                                                            }
                                                        </Text>
                                                        {
                                                            !enabledLoading ?
                                                                <Text as="p">
                                                                    It will not affect your theme's visuals. <a href="https://uniocode.gitbook.io/stack-discounts/1.-getting-started" rel="noreferrer" target="_blank" className="Polaris-Link Polaris-Link--monochrome ml-5">Learn more</a>
                                                                </Text>
                                                                : null
                                                        }
                                                    </BlockStack>
                                                    {
                                                        enabledLoading ?
                                                            <Button
                                                                onClick={() => {
                                                                    if (props.user.viewOnly) return;

                                                                    setStateLoading(true);
                                                                    getMainThemeSettingsData();
                                                                }}
                                                                loading={stateLoading}
                                                                variant="secondary"
                                                            >Recheck state</Button>
                                                            :
                                                            <Button
                                                                onClick={() => {
                                                                    if (props.user.viewOnly) return;

                                                                    setEnabledLoading(true);
                                                                    window.open(props.user.extensionDeepLink, '_blank');
                                                                }}
                                                                icon={ExternalSmallIcon}
                                                                variant="primary"
                                                            >Enable app</Button>
                                                    }
                                                </InlineStack>
                                            </Banner>
                                        </BlockStack>
                                    </Card>
                                </div>
                            </Layout.Section>
                            : null
                    }
                    <Layout.Section>
                        <LegacyCard sectioned>
                            <FormLayout>
                                <TextField
                                    value={model.displayName ?? ''}
                                    onChange={(newValue) => handleChange('displayName', newValue)}
                                    label="Campaign name"
                                    error={error.displayName ? 'Display name is required' : (error.titleUnique ? 'Shop already has an automatic discount with this title. Try a new title.' : '')}
                                    onBlur={() => handleError('displayName', !model.displayName)}
                                    placeholder="e.g. Black friday deals"
                                />
                                <TextField
                                    value={model.displayDescription ?? ''}
                                    onChange={(newValue) => handleChange('displayDescription', newValue)}
                                    label="Campaign description (optional)"
                                    multiline={4}
                                //error={error.displayDescription ? 'Display description is required' : ''}
                                //onBlur={() => handleError('displayDescription', !model.displayDescription)}
                                />
                            </FormLayout>
                        </LegacyCard>
                        <LegacyCard sectioned title="Method">
                            <FormLayout>
                                <BlockStack gap="200">
                                    <RadioButton
                                        label="Automatic"
                                        helpText="Combine multiple discounts into one campaign that applies automatically."
                                        checked={model.campaignMethodId === 1}
                                        name="campaignFormat"
                                        onChange={(newValue) => {
                                            handleChange('automatic', true);
                                            handleChange('bulk', false);
                                            handleChange('bulkCodeGenerationType', null);
                                            handleChange('campaignMethodId', 1);
                                            handleError('titleUnique', false);
                                            handleError('code', false);
                                            handleError('codeUnique', false);
                                            handleError('codeCount', false);
                                            if (!selectedCombinesWith.includes(combinesWithFlags.campaigns)) {
                                                let cw = [...selectedCombinesWith];
                                                cw.push(combinesWithFlags.campaigns)
                                                handleCombinesWithChange(cw);
                                            }
                                            setCombinesWithDisabled(false);
                                        }}
                                    />
                                    {
                                        showAutomaticStandalone() ?
                                            <RadioButton
                                                label="Automatic (Independent)"
                                                helpText="Combine multiple discounts into one independent campaign that applies automatically."
                                                checked={model.campaignMethodId === 4}
                                                name="campaignFormat"
                                                onChange={(newValue) => {
                                                    handleChange('automatic', true);
                                                    handleChange('bulk', false);
                                                    handleChange('bulkCodeGenerationType', null);
                                                    handleChange('campaignMethodId', 4);
                                                    handleError('code', false);
                                                    handleError('codeUnique', false);
                                                    handleError('codeCount', false);
                                                    if (selectedCombinesWith.includes(combinesWithFlags.campaigns)) {
                                                        const cw = selectedCombinesWith.filter(x => x !== combinesWithFlags.campaigns);
                                                        handleCombinesWithChange(cw);
                                                    }
                                                    setCombinesWithDisabled(false);
                                                }}
                                            />
                                            : null
                                    }
                                    <BlockStack gap="100">
                                        <RadioButton
                                            label="Code"
                                            helpText="A single code can apply multiple discounts within the same campaign."
                                            name="campaignFormat"
                                            checked={model.campaignMethodId === 2}
                                            onChange={(newValue) => {
                                                handleChange('automatic', false);
                                                handleChange('bulk', false);
                                                handleChange('bulkCodeGenerationType', null);
                                                handleChange('campaignMethodId', 2);
                                                handleError('titleUnique', false);
                                                handleError('codeCount', false);
                                                if ((model.combinesWith & combinesWithFlags.campaigns) !== 0 && !showCombinesWith()) {
                                                    setCombinesWithDisabled(true);
                                                    let cw = [...selectedCombinesWith];
                                                    const toRemove = [4, 8]; //product and order discounts
                                                    cw = cw.filter(x => !toRemove.includes(x));
                                                    handleCombinesWithChange(cw);
                                                }
                                            }}
                                        />
                                        {
                                            model.campaignMethodId === 2 ?
                                                <Box paddingInlineStart="600" paddingInlineEnd="200">
                                                    <TextField
                                                        value={model.code ?? ''}
                                                        placeholder="e.g. SPRINGSALE"
                                                        onChange={(newValue) => handleChange('code', newValue)}
                                                        error={error.code ? 'Campaign code is required' : (error.codeUnique ? 'Shop already has a discount with this code. Try a new code.' : '')}
                                                        onBlur={() => handleError('code', !model.code)}
                                                        connectedRight={
                                                            <Tooltip content="Copy code">
                                                                <Button disabled={!model.code} size="large" icon={ClipboardIcon} onClick={(e) => copyToClipboard(model.code ?? '', e)} />
                                                            </Tooltip>
                                                        }
                                                    />
                                                </Box>
                                                : null
                                        }
                                    </BlockStack>
                                    <BlockStack gap="300">
                                        <RadioButton
                                            label="Bulk codes"
                                            helpText="Generate multiple unique codes, all powered by the same campaign logic."
                                            name="campaignFormat"
                                            checked={model.campaignMethodId === 3}
                                            onChange={(newValue) => {
                                                handleChange('automatic', false);
                                                handleChange('bulk', true);
                                                handleChange('bulkCodeGenerationType', 2);
                                                handleChange('campaignMethodId', 3);
                                                handleError('titleUnique', false);
                                                handleError('code', false);
                                                handleError('codeUnique', false);
                                                if ((model.combinesWith & combinesWithFlags.campaigns) !== 0 && !showCombinesWith()) {
                                                    setCombinesWithDisabled(true);
                                                    let cw = [...selectedCombinesWith];
                                                    const toRemove = [4, 8]; //product and order discounts
                                                    cw = cw.filter(x => !toRemove.includes(x));
                                                    handleCombinesWithChange(cw);
                                                }
                                            }}
                                        />
                                        {
                                            model.campaignMethodId === 3 ?
                                                <Box paddingInlineStart="600" paddingInlineEnd="200">
                                                    <BlockStack gap="200">
                                                        <LegacyStack alignment="center">
                                                            <LegacyStack.Item fill>
                                                                <BlockStack gap="200">
                                                                    <Text fontWeight="semibold">Codes: {model.codeCount}</Text>
                                                                    <InlineStack gap="200">
                                                                        <Button onClick={() => {
                                                                            clearSelection();
                                                                            setModalViewBulkCodesActive(true);
                                                                            loadBulkCodes();
                                                                        }} disabled={!model.codeCount || model.bulkCreationInProgress} variant="plain">
                                                                            View all codes
                                                                        </Button>
                                                                        |
                                                                        <Button onClick={() => {
                                                                            window.open(`${props.user.shopifyAdminUrl}/discounts/${model.discountId}/codes`, '_blank');
                                                                        }} disabled={!model.codeCount || model.bulkCreationInProgress} variant="plain">
                                                                            Filter & export
                                                                        </Button>
                                                                    </InlineStack>
                                                                </BlockStack>
                                                            </LegacyStack.Item>
                                                            <LegacyStack.Item>
                                                                {
                                                                    model.bulkCodes?.length && !model.bulkCreationInProgress ?
                                                                        <Box
                                                                            padding="100"
                                                                            paddingInlineStart="300"
                                                                            background="bg-surface-secondary"
                                                                            borderRadius="200"
                                                                            shadow="md"
                                                                        >
                                                                            <InlineStack gap="200" blockAlign="center">
                                                                                <Text>{model.bulkCodes?.length} {model.codeCount > 0 ? 'more' : ''} code{model.bulkCodes?.length > 1 ? 's' : ''} will be added</Text>
                                                                                <Button onClick={() => { setModalAddBulkCodesActive(true) }}>
                                                                                    Modify
                                                                                </Button>
                                                                            </InlineStack>
                                                                        </Box>
                                                                        : null
                                                                }
                                                                {
                                                                    !model.bulkCodes?.length && !model.bulkCreationInProgress ?
                                                                        <Button onClick={() => { setModalAddBulkCodesActive(true) }} icon={PlusIcon}>
                                                                            Add {model.codeCount > 0 ? 'more' : ''} codes
                                                                        </Button>
                                                                        : null
                                                                }
                                                                {
                                                                    model.bulkCreationInProgress ?
                                                                        <InlineStack gap="100">
                                                                            <Spinner size="small" />
                                                                            <Text>Code processing in progress...</Text>
                                                                        </InlineStack>
                                                                        : null
                                                                }
                                                            </LegacyStack.Item>
                                                        </LegacyStack>
                                                        {
                                                            error.codeCount ?
                                                                <InlineError message="At least one campaign code is required" />
                                                                : null
                                                        }
                                                    </BlockStack>
                                                </Box>
                                                : null
                                        }
                                    </BlockStack>
                                </BlockStack>
                            </FormLayout>
                        </LegacyCard>
                        <LegacyCard sectioned title="Type">
                            <FormLayout>
                                <ChoiceList
                                    choices={[
                                        { label: 'Combination', value: 1 },
                                        { label: 'Reduction', value: 5 },
                                        { label: 'First Match', value: 6 },
                                        { label: 'Best Match', value: 7 }
                                    ]}
                                    selected={[model.campaignTypeId]}
                                    onChange={(newValue) => handleChange('campaignTypeId', newValue[0])}
                                />
                                {
                                    model.campaignTypeId === 1 ?
                                        <List>
                                            <List.Item><Text as="p" tone="subdued">All discounts will be automatically applied to items in the cart.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">If multiple discounts are eligible for the same item, only the one with the highest value will be applied to that item to prevent unusually high or 100% discounts.</Text></List.Item>
                                        </List>
                                        : null
                                }
                                {
                                    model.campaignTypeId === 5 ?
                                        <List>
                                            <List.Item><Text as="p" tone="subdued">Discounts will be applied in the specified order listed in the 'Discounts' section.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">Even if multiple discounts are eligible for the same item, each discount will be applied consecutively, one after the other, to the already discounted amount.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">Exercise caution when implementing these types of campaigns. Relying solely on fixed amount discounts could inadvertently result in providing a 100% discount to customers.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">You can reorder discounts using the left-side arrows in the list.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">{"All automatic campaigns of this type are executed first, in the order specified on the Campaigns page."}</Text></List.Item>
                                        </List>
                                        : null
                                }
                                {
                                    model.campaignTypeId === 6 ?
                                        <List>
                                            <List.Item><Text as="p" tone="subdued">Discounts will be evaluated in a specified order listed in the 'Discounts' section.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">The first eligible discount will be applied, and all others will be ignored.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">You can reorder discounts using the left-side arrows in the list.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">{"All automatic campaigns of this type are executed first, in the order specified on the Campaigns page."}</Text></List.Item>
                                        </List>
                                        : null
                                }
                                {
                                    model.campaignTypeId === 7 ?
                                        <List>
                                            <List.Item><Text as="p" tone="subdued">All discounts will be evaluated.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">The discount with maximum value will be applied, and all others will be ignored.</Text></List.Item>
                                            <List.Item><Text as="p" tone="subdued">{"All automatic campaigns of this type are executed first, in the order specified on the Campaigns page."}</Text></List.Item>
                                        </List>
                                        : null
                                }
                                {
                                    model.campaignMethodId === 1 ?
                                        <Banner status="warning">
                                            <p>For automatic campaigns this setting is common and change will reflect on all of them.</p>
                                        </Banner>
                                        : null
                                }
                            </FormLayout>
                        </LegacyCard>
                        <LegacyCard sectioned title="Customer eligibility">
                            <FormLayout>
                                <BlockStack gap="300">
                                    <ChoiceList
                                        choices={customerTypes}
                                        selected={[customerType]}
                                        onChange={(newValue) => {
                                            setCustomerType(newValue[0]);
                                            handleChange('allCustomers', newValue[0] === 0);
                                            handleError('customerType', false);
                                        }}
                                    />
                                    <Divider />
                                    <BlockStack gap="50">
                                        <Checkbox
                                            label="Require customer to be logged in"
                                            checked={model.requiresCustomerLogin}
                                            onChange={(newValue) => {
                                                if (newValue && model.showBeforeCustomerIdentified) {
                                                    handleChange('showBeforeCustomerIdentified', false);
                                                }
                                                handleChange('requiresCustomerLogin', newValue);
                                            }}
                                        />
                                        {
                                            customerType !== 0 && (props.user.myShopifyDomain === "cerebroefecto.myshopify.com"
                                                || props.user.myShopifyDomain === "brain-effect.myshopify.com"
                                                || props.user.myShopifyDomain === "discounts-07.myshopify.com"
                                                || props.user.myShopifyDomain === "discounts-02.myshopify.com") ?
                                                <Checkbox
                                                    label="Show before customer is identified"
                                                    checked={model.showBeforeCustomerIdentified}
                                                    disabled={model.requiresCustomerLogin}
                                                    onChange={(newValue) => handleChange('showBeforeCustomerIdentified', newValue)}
                                                />
                                                : null
                                        }
                                    </BlockStack>
                                </BlockStack>
                            </FormLayout>
                        </LegacyCard>
                        <LegacyCard sectioned title="Sales channel access">
                            <FormLayout>
                                <ChoiceList
                                    choices={[
                                        { label: 'All sales channels', value: 0 },
                                        { label: 'Only Online Store', value: 1, renderChildren: onlyOnlineStore },
                                        { label: 'Except Online Store', value: 2, renderChildren: expectOnlineStore }
                                    ]}
                                    selected={[salesChannel]}
                                    onChange={(newValue) => {
                                        setSalesChannel(newValue[0]);
                                        handleChange('salesChannels', newValue[0] === 0 ? null : newValue[0].toString());
                                    }}
                                />
                            </FormLayout>
                        </LegacyCard>
                        {
                            props.user.myShopifyDomain === "discounts-02.myshopify.com"
                                || props.user.myShopifyDomain === "sandbox-sail.myshopify.com"
                                || props.user.myShopifyDomain === "sandbox-sail-aaf.myshopify.com"
                                || props.user.myShopifyDomain === "sail-plein-air-aaf.myshopify.com"
                                || props.user.myShopifyDomain === "f2808m-02.myshopify.com"
                                || props.user.myShopifyDomain === "smartwool-amer-dev.myshopify.com"
                                || props.user.myShopifyDomain === "smartwool-amer.myshopify.com"
                                || props.user.myShopifyDomain === "lifevac-usa.myshopify.com"
                                || props.user.myShopifyDomain === "discounts-07.myshopify.com" ?
                                <LegacyCard sectioned title="Sale price handling">
                                    <FormLayout>
                                        <BlockStack gap="200">
                                            <Checkbox
                                                label="Override global setting"
                                                checked={model.salePriceHandlingEnabled}
                                                onChange={(newValue) => {
                                                    handleChange('salePriceHandlingEnabled', newValue);
                                                    if (!newValue) {
                                                        handleChange('salePriceHandling', model.salePriceHandlingGlobal);
                                                    }
                                                }}
                                            />
                                            <BlockStack gap="100">
                                                <InlineGrid gap="300" columns={{ xs: 1, sm: 2 }}>
                                                    <Select
                                                        options={[{ label: 'Apply discounts on sale price', value: 0 }, { label: 'Apply discounts on original price', value: 1 }, { label: 'Skip discounts for products on sale', value: 2 }]}
                                                        value={model.salePriceHandling}
                                                        onChange={(newValue) => handleChange('salePriceHandling', newValue)}
                                                        disabled={!model.salePriceHandlingEnabled}
                                                    />
                                                    <div></div>
                                                </InlineGrid>
                                                <Text as="p" tone="subdued">
                                                    {
                                                        model.salePriceHandling === 0 ? 'If some of your products are on sale, discounts will be applied to their sale price.'
                                                            :
                                                            (
                                                                model.salePriceHandling === 1
                                                                    ? 'If some of your products are on sale, discounts will be applied to their original price. Discounts will not apply to an item if the sale amount is greater than the discount amount.'
                                                                    : 'If some of your products are on sale, discounts will not apply to them.'
                                                            )

                                                    }
                                                </Text>
                                            </BlockStack>
                                        </BlockStack>
                                    </FormLayout>
                                </LegacyCard>
                                : null
                        }
                        {
                            model.campaignMethodId === 2 || model.campaignMethodId === 3 || showRecurringCycleLimit() ?
                                <LegacyCard sectioned>
                                    <FormLayout>
                                        {
                                            model.campaignMethodId === 2 || model.campaignMethodId === 3 ?
                                                <BlockStack gap="200">
                                                    <Text variant="headingSm" as="h2">Maximum discount uses</Text>
                                                    <BlockStack gap="0">
                                                        <Checkbox
                                                            label="Limit number of times this discount can be used in total"
                                                            checked={activeUsageLimit}
                                                            onChange={(newValue) => handleActiveUsageLimit(newValue)}
                                                        />
                                                        {
                                                            activeUsageLimit ?
                                                                <Box paddingInlineStart="600" paddingBlockEnd="100">
                                                                    <div className="choice-list-input">
                                                                        <QuantityFieldCustom
                                                                            fieldName="usageLimit"
                                                                            value={model.usageLimit}
                                                                            required={true}
                                                                            error={error.usageLimit ? "Total usage limit can't be blank" : ''}
                                                                            handleChange={handleChange}
                                                                            handleError={handleError}
                                                                        />
                                                                    </div>
                                                                </Box>
                                                                : null
                                                        }
                                                        <Checkbox
                                                            label="Limit to one use per customer"
                                                            checked={model.appliesOncePerCustomer}
                                                            onChange={(newValue) => handleChange('appliesOncePerCustomer', newValue)}
                                                        />
                                                    </BlockStack>
                                                </BlockStack>
                                                : null
                                        }
                                        {
                                            showRecurringCycleLimit() ?
                                                <BlockStack gap="300">
                                                    <BlockStack gap="200">
                                                        <Text variant="headingSm" as="h2">Recurring payments for subscriptions</Text>
                                                        <ChoiceList
                                                            choices={[
                                                                { label: 'Limit discounts to the first payment', value: 0 },
                                                                { label: 'Limit discounts to multiple recurring payments', value: 1, renderChildren: renderRecurringCycleLimit },
                                                                { label: 'Discounts apply to all recurring payments', value: 2 }
                                                            ]}
                                                            selected={[model.recurringCycleOption]}
                                                            onChange={(newValue) => {
                                                                handleChange('recurringCycleOption', newValue[0]);
                                                            }}
                                                        />
                                                    </BlockStack>
                                                    {
                                                        model.campaignMethodId === 1 ?
                                                            <Banner status="warning">
                                                                <p>For automatic campaigns this setting is common and change will reflect on all of them.</p>
                                                            </Banner>
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                    </FormLayout>
                                </LegacyCard>
                                : null
                        }
                        <LegacyCard sectioned title="Combines with">
                            <FormLayout>
                                <BlockStack gap="0">
                                    <div id="combinesWithChoiceList">
                                        <ChoiceList
                                            allowMultiple
                                            choices={[
                                                { label: model.campaignMethodId === 1 || model.campaignMethodId === 4 ? 'Campaigns with same type' : 'Automatic campaigns with same type', disabled: model.campaignMethodId === 1 || model.campaignMethodId === 4, value: combinesWithFlags.campaigns, helpText: "Other non-app discounts created directly in Shopify admin and marked as combinable." },
                                                { label: 'Shipping discounts', value: combinesWithFlags.shippingDiscounts },
                                                { label: 'Product discounts', value: combinesWithFlags.productDiscounts, disabled: combinesWithDisabled, renderChildren: props.user.shopifyPlus ? renderApplyWithDiscountTags : null },
                                                { label: 'Order discounts', value: combinesWithFlags.orderDiscounts, disabled: combinesWithDisabled }
                                            ]}
                                            selected={selectedCombinesWith}
                                            onChange={(newValue) => {
                                                if (!newValue.includes(combinesWithFlags.campaigns) && combinesWithDisabled) {
                                                    setCombinesWithDisabled(false);
                                                }
                                                else if (model.campaignMethodId !== 1 && model.campaignMethodId !== 4 && newValue.includes(combinesWithFlags.campaigns) && !showCombinesWith() && !combinesWithDisabled) {
                                                    const toRemove = [4, 8]; //product and order discounts
                                                    newValue = newValue.filter(x => !toRemove.includes(x));
                                                    setCombinesWithDisabled(true);
                                                }

                                                handleCombinesWithChange(newValue);
                                            }}
                                        />
                                    </div>
                                </BlockStack>
                                {
                                    model.campaignMethodId === 1 ?
                                        <Banner status="warning">
                                            <p>For automatic campaigns this setting is common and change will reflect on all of them.</p>
                                        </Banner>
                                        : null
                                }
                            </FormLayout>
                        </LegacyCard>
                        <LegacyCard sectioned title="Active dates">
                            <FormLayout>
                                <FormLayout.Group>
                                    <DatePickerCustom
                                        label="Start date"
                                        fieldName="startDate"
                                        value={model.startDate}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                    <TimePickerCustom
                                        label={'Start time (' + model.timezoneAbbreviation + ')'}
                                        fieldName="startTime"
                                        value={model.startTime}
                                        handleChange={handleChange}
                                        handleError={handleError}
                                    />
                                </FormLayout.Group>
                                <Checkbox
                                    label="Set end date"
                                    checked={model.hasEndDate}
                                    onChange={(newValue) => handleChange('hasEndDate', newValue)}
                                />
                                {
                                    model.hasEndDate ?
                                        <FormLayout.Group>
                                            <DatePickerCustom
                                                label="End date"
                                                fieldName="endDate"
                                                value={model.endDate}
                                                startDate={model.startDate}
                                                startTime={model.startTime}
                                                endTime={model.endTime}
                                                handleChange={handleChange}
                                                handleError={handleError}
                                            />
                                            <TimePickerCustom
                                                label={'End time (' + model.timezoneAbbreviation + ')'}
                                                fieldName="endTime"
                                                value={model.endTime}
                                                handleChange={handleChange}
                                                handleError={handleError}
                                            />
                                        </FormLayout.Group>
                                        : null
                                }
                                {
                                    error.wrongStartEndDates ?
                                        <InlineError message="Start date should be before end date" />
                                        : null
                                }
                            </FormLayout>
                        </LegacyCard>
                        <CampaignDiscount ref={campaignDiscountRef} currencySymbol={getCurrencySymbol(props.user.moneyFormat)} handleSaveDiscount={handleSaveDiscount} isEmbed={props.isEmbed} user={props.user} automatic={model.campaignMethodId === 1 || model.campaignMethodId === 4} />
                        <LegacyCard sectioned title="Discounts" actions={[{
                            content: 'Add discount',
                            onAction: () => { campaignDiscountRef.current.open(null) },
                            disabled: (model.isFreePlan && model.discounts.length >= 2)
                        }]}>
                            {
                                (!model.discounts || !model.discounts.length) ?
                                    <InlineError message={'Add at least one discount.'} fieldID="discountsEnabled" />
                                    : null
                            }
                            <div id={"campaign-discounts"}>
                                {
                                    model.isFreePlan ?
                                        <Banner
                                            title="Free plan limitations"
                                            status="warning"
                                        >
                                            <p>You can stack a maximum of 2 discounts here.</p>
                                            {/*<p>Also, you can have a maximum of 1 campaign (with a maximum of 2 discounts assigned) in <Link url="/campaigns">Campaigns</Link>.</p>*/}
                                            <p>You can always upgrade to a paid plan on the <Link url="/settings/billing">Billing</Link> page.</p>
                                        </Banner>
                                        : null
                                }
                                {
                                    model.isFreePlan && model.discounts.length > 2 ?
                                        <Banner
                                            title="Free plan limitations exceeded."
                                            status="critical"
                                        >
                                            <p>Free plan limitations are not met. You need to remove some discounts or upgrade to paid plan on the <Link monochrome url="/settings/billing">Billing</Link> page.</p>
                                        </Banner>
                                        : null
                                }
                                <ResourceList
                                    emptyState={
                                        <EmptyState
                                            heading="Add and stack multiple discounts"
                                            action={{ content: 'Add discount', onAction: () => campaignDiscountRef.current.open(null) }}
                                            image={discountsEmptyStateIconCustom}
                                        >
                                            <p>You can add one or more discounts to this campaign.</p>
                                        </EmptyState>
                                    }
                                    resourceName={{ singular: 'discount', plural: 'discounts' }}
                                    items={model.discounts ? orderedDiscounts() : []}
                                    renderItem={(item, id, index) => {
                                        const count = model.discounts.length;

                                        return (
                                            <ResourceItem
                                                onClick={() => campaignDiscountRef.current.open({
                                                    id: id,
                                                    index: index,
                                                    discountType: item.discountType,
                                                    discountSubType: item.discountSubType,
                                                    title: item.title,
                                                    valueType: item.valueType,
                                                    valueFixedAmount: item.valueType === 2 ? item.value : '',
                                                    valueFixedPrice: item.valueType === 3 ? item.value : '',
                                                    valuePercentage: item.valueType === 1 ? item.value * 100 : '',
                                                    getType: item.getProductResources !== null && item.getProductResources.length > 0 ? 2 : (item.getCollectionResources !== null && item.getCollectionResources.length > 0 ? 1 : 0),
                                                    buyType: item.buyProductResources !== null && item.buyProductResources.length > 0 ? 2 : (item.buyCollectionResources !== null && item.buyCollectionResources.length > 0 ? 1 : 0),
                                                    excludeType: item.excludeProductResources !== null && item.excludeProductResources.length > 0 ? 2 : (item.excludeCollectionResources !== null && item.excludeCollectionResources.length > 0 ? 1 : 0),
                                                    getCollectionResources: JSON.parse(JSON.stringify(item.getCollectionResources)),
                                                    getProductResources: JSON.parse(JSON.stringify(item.getProductResources)),
                                                    buyCollectionResources: JSON.parse(JSON.stringify(item.buyCollectionResources)),
                                                    buyProductResources: JSON.parse(JSON.stringify(item.buyProductResources)),
                                                    excludeCollectionResources: JSON.parse(JSON.stringify(item.excludeCollectionResources)),
                                                    excludeProductResources: JSON.parse(JSON.stringify(item.excludeProductResources)),
                                                    minRequirements: getMinRequirements(item),
                                                    minimumAmount: item.minimumAmount,
                                                    minimumQuantity: item.minimumQuantity,
                                                    maxLimits: getMaxLimits(item),
                                                    maximumAmount: item.maximumAmount,
                                                    maximumQuantity: item.maximumQuantity,
                                                    skipQuantity: item.skipQuantity,
                                                    onQuantity: item.onQuantity,
                                                    discountedValueType: item.valueType === 2 ? 2 : (item.valueType === 3 ? 4 : (item.valueType === 1 && item.value === 1 ? 3 : 1)),
                                                    setUsesPerOrderLimit: item.usesPerOrderLimit !== null,
                                                    usesPerOrderLimit: item.usesPerOrderLimit,
                                                    appliesOnEachItem: item.appliesOnEachItem !== null ? item.appliesOnEachItem : false,
                                                    countryType: item.allCountries === null || item.allCountries ? 0 : 1,
                                                    setMaximumShippingPrice: item.maximumShippingPrice !== null,
                                                    maximumShippingPrice: item.maximumShippingPrice,
                                                    countries: item.countries,
                                                    includeRestOfWorld: item.includeRestOfWorld,
                                                    shippingMethodType: !item.shippingMethods ? 0 : 1,
                                                    shippingMethods: item.shippingMethods,
                                                    itemRequirements: ((item.buyProductResources !== null && item.buyProductResources.length > 0) || (item.buyCollectionResources !== null && item.buyCollectionResources.length > 0)) ? 1 : (((item.getProductResources !== null && item.getProductResources.length > 0) || (item.getCollectionResources !== null && item.getCollectionResources.length > 0)) ? 2 : 0),
                                                    purchaseType: item.purchaseType,
                                                    autoAdd: item.autoAdd !== null ? item.autoAdd : false,
                                                    tiers: item.discountSubType === 5 ? (item.tiers.map(p => ({
                                                        id: p.id,
                                                        title: p.title,
                                                        minimumAmount: p.minimumAmount,
                                                        minimumQuantity: p.minimumQuantity,
                                                        valueFixedAmount: item.valueType === 2 ? p.value : '',
                                                        valuePercentage: item.valueType === 1 ? p.value * 100 : '',
                                                    }))) : null,
                                                    mixes: item.discountSubType === 6 ? (item.mixes.map(p => ({
                                                        id: p.id,
                                                        getType: p.getProductResources !== null && p.getProductResources.length > 0 ? 2 : (p.getCollectionResources !== null && p.getCollectionResources.length > 0 ? 1 : 0),
                                                        getCollectionResources: JSON.parse(JSON.stringify(p.getCollectionResources)),
                                                        getProductResources: JSON.parse(JSON.stringify(p.getProductResources)),
                                                        purchaseType: p.purchaseType,
                                                        valueType: p.appliesOnEachItem ? 4 : p.valueType,
                                                        valueFixedAmount: p.valueType === 2 || p.valueType === 4 ? p.value : '',
                                                        valueFixedPrice: p.valueType === 3 ? p.value : '',
                                                        valuePercentage: p.valueType === 1 ? p.value * 100 : '',
                                                        appliesOnEachItem: p.appliesOnEachItem !== null ? p.appliesOnEachItem : false,
                                                    }))) : null
                                                })}
                                                shortcutActions={[
                                                    {
                                                        content: <Icon source={DuplicateIcon} />,
                                                        onAction: () => { handleDuplicateDiscount(index) }
                                                    },
                                                    {
                                                        content: <Icon source={DeleteIcon} />,
                                                        destructive: true,
                                                        onAction: () => { handleRemoveDiscount(index) }
                                                    },
                                                ]}
                                                persistActions
                                            >
                                                <InlineStack wrap={false}>
                                                    <div>
                                                        <div className="order-button">
                                                            <Button variant="plain" disabled={index === 0} onClick={(e) => handleSeqChange(e, index, true)}><Icon source={ArrowUpIcon} /></Button>
                                                        </div>
                                                        <div className="order-button">
                                                            <Button variant="plain" disabled={index === count - 1} onClick={(e) => handleSeqChange(e, index, false)}><Icon source={ArrowDownIcon} /></Button>
                                                        </div>
                                                    </div>
                                                    <div className="w-100">
                                                        <Text variant="bodyMd" fontWeight="semibold" as="h3">{item.title}</Text>
                                                        <div>{discountSubTypes.filter(p => p.value === item.discountSubType)[0].name}</div>
                                                    </div>
                                                </InlineStack>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                            </div>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section variant="oneThird">
                        <LegacyCard title="Summary" subdued={true}>
                            <LegacyCard.Section>
                                <LegacyStack wrap={false}
                                    spacing="tight">
                                    <LegacyStack.Item fill>
                                        <Text as="span" fontWeight="semibold">{model.displayName}</Text>
                                    </LegacyStack.Item>
                                    <LegacyStack.Item>
                                        <Text as="span"><Badge tone={model.statusName === "Active" ? "success" : (model.statusName === "Scheduled" ? "attention" : "default")}>{model.statusName}</Badge></Text>
                                    </LegacyStack.Item>
                                </LegacyStack>
                                {
                                    model.campaignMethodId === 2 && model.code ?
                                        <Box paddingBlockStart="100">
                                            <LegacyStack wrap={false} alignment="center">
                                                <LegacyStack.Item>
                                                    <Text as="span" tone="subdued">Code: </Text>
                                                    <Text as="span">{model.code}</Text>
                                                </LegacyStack.Item>
                                                <LegacyStack.Item>
                                                    <div className="display-flex-span">
                                                        <Tooltip content="Copy code">
                                                            <Button disabled={!model.code} size="large" variant="tertiary" icon={ClipboardIcon} onClick={(e) => copyToClipboard(model.code ?? '', e)} />
                                                        </Tooltip>
                                                    </div>
                                                </LegacyStack.Item>
                                            </LegacyStack>
                                        </Box>
                                        : null
                                }
                                <div className="campaign-summary-list">
                                    <List type="bullet">
                                        <List.Item>{'Method: ' + (model.campaignMethodId === 1 ? 'Automatic' : (model.campaignMethodId === 2 ? 'Code' : (model.campaignMethodId === 3 ? 'Bulk codes' : 'Automatic (Independent)')))}</List.Item>
                                        <List.Item>{model.discounts.length + ' discount' + (model.discounts.length > 1 ? 's' : '')}</List.Item>
                                        <List.Item>{model.dateRange}</List.Item>
                                    </List>
                                </div>
                            </LegacyCard.Section>
                            <LegacyCard.Section sectioned title="PERFORMANCE">
                                <List type="bullet">
                                    <List.Item>{model.performance.impressions + ' impressions'}</List.Item>
                                    <List.Item>{model.performance.applies + ' applies'}</List.Item>
                                    <List.Item>{model.performance.converted + ' converted'}</List.Item>
                                    <List.Item><MoneyFormatCustom price={model.performance.discountAmount} format={props.user.moneyFormat} /> discount provided</List.Item>
                                </List>
                            </LegacyCard.Section>
                        </LegacyCard>
                        <LegacyCard title="Tags" subdued={true}>
                            <LegacyCard.Section>
                                <BlockStack gap="400">
                                    <DiscountTagsPickerCustom
                                        fieldName="discountTags"
                                        value={model.discountTags}
                                        handleChange={handleChange}
                                        placeholder="Search or add tags"
                                        allowAddNew={true}
                                    />
                                    {
                                        model.campaignMethodId === 1 ?
                                            <Banner status="warning">
                                                <p>For automatic campaigns this setting is common and change will reflect on all of them.</p>
                                            </Banner>
                                            : null
                                    }
                                </BlockStack>
                            </LegacyCard.Section>
                        </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <PageActions primaryAction={{
                            content: 'Save', loading: props.isLoading, onAction: () => {
                                if (props.user.viewOnly) return;
                                saveChanges();
                            }
                        }} secondaryActions={editMode ? [] : [
                            {
                                content: 'Discard',
                                icon: DeleteIcon,
                                onAction: () => { discardChanges() },
                            }
                        ]} />
                    </Layout.Section>
                </Layout>
            </Page >
    );
}